/**
 * SMS Automation Stats Page
 * Route: /marketing/automations/sms/:id/stats
 *
 * Styled after EmailAutomationStatsPage: header with created date + date range,
 * contact-count link + refresh button, stat-card summaries (sends AND contact
 * statuses), expandable per-step analytics with a message-content modal,
 * details grid with a clickable entry list, pause/resume controls.
 *
 * Every box on the page is clickable and opens the contacts behind its
 * number: a step card's boxes filter to that step (by stepId — see the
 * contacts page) and that outcome or lead, the summary boxes to that outcome,
 * contact status or lead kind.
 */
import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Loader2 } from 'lucide-react';
import { SmsPhoneMockup } from '@/components/sms-editor';
import { PauseAutomationDialog } from '@/components/marketing/PauseAutomationDialog';
import { MarketingHistoryCard } from '@/components/marketing/MarketingHistoryCard';
import { RemovedStepsSection, describeRemoval, type StepRemoval } from '@/components/marketing/RemovedStepsSection';
import * as marketingSvc from '@/services/marketing.service';
import type { RequestInfoLeadCounts } from '@/services/marketing.service';
import { DatePicker } from '@/components/ui/DatePicker';
import { useUserTimezone } from '@/hooks/useUserTimezone';
import { statBox, contactsPath } from '@/components/marketing/statBox';
import { cn } from '@/lib/utils';

interface StepStat {
  stepId: number;
  stepOrder: number;
  delayMinutes: number;
  messagePreview: string;
  messageBody?: string;
  mediaUrls?: string[];
  totalSends: number;
  sent: number;
  delivered: number;
  failed: number;
  undelivered: number;
  /** Request-info leads credited to this step. Absent from older API builds. */
  leads?: RequestInfoLeadCounts;
}

type RemovedStepStat = StepStat & StepRemoval;

function fmtNum(n: number | null | undefined): string {
  if (n == null) return '0';
  return n.toLocaleString();
}

function formatCreatedDate(dateStr: string | null | undefined): string {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return '';
  const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  return `${days[d.getDay()]}, ${months[d.getMonth()]} ${d.getDate()}, ${d.getFullYear()}`;
}

function formatDelayText(minutes: number, isFirst = false): string {
  if (minutes === 0) return isFirst ? 'Immediately' : 'Immediately after previous';
  if (minutes < 60) return `${minutes} minute${minutes !== 1 ? 's' : ''}`;
  if (minutes < 1440) {
    const h = Math.round(minutes / 60 * 10) / 10;
    return `${h} hour${h !== 1 ? 's' : ''}`;
  }
  const d = Math.round(minutes / 1440 * 10) / 10;
  return `${d} day${d !== 1 ? 's' : ''}`;
}

const EXIT_CRITERIA_LABELS: Record<number, string> = {
  0: 'Contacts have received all messages in the automation',
  1: 'Contacts are removed from the list',
};

/**
 * The two rows below count different things and used to share the word "Failed",
 * which made them look like the same number disagreeing with itself.
 *
 * SEND_CARDS count rows in sms_automation_sends — message ATTEMPTS, and the same
 * thing the per-step numbers count. One contact contributes several when a
 * transient failure is retried. They honour the date range.
 *
 * CONTACT_CARDS count rows in sms_automation_contacts — PEOPLE, by where the
 * sequence left them. A permanently-failed send stops the contact as "Invalid
 * number", never as "failed", so these two rows do not reconcile line by line
 * and are not meant to. They are current state, so the date range does not
 * apply to them.
 */
const SEND_CARDS = [
  // 'sent,delivered' because that is what Messages Sent counts — a delivered
  // message was also sent.
  { key: 'sent' as const, label: 'Messages Sent', color: '#2e7d32', link: { sendStatus: 'sent,delivered' } },
  { key: 'delivered' as const, label: 'Delivered', color: '#8e44ad', link: { sendStatus: 'delivered' } },
  { key: 'failed' as const, label: 'Failed Attempts', color: '#dc3545', link: { sendStatus: 'failed' } },
  { key: 'undelivered' as const, label: 'Undelivered', color: '#ff9800', link: { sendStatus: 'undelivered' } },
];

/**
 * Request-info leads — a third thing again: requests made through a link in
 * these messages, the automation's own contacts and whoever they forwarded the
 * link to, together. It ends the People row (its caption says Leads follow
 * the dates while the rest is all time) and opens "Created a lead", either
 * kind.
 */
const LEAD_CARD = { label: 'Leads', color: '#fd7e14', link: { lead: '1' } };

const CONTACT_CARDS = [
  { key: 'total' as const, label: 'Total Contacts', color: '#2e7d32', link: {} },
  { key: 'active' as const, label: 'Active', color: '#28a745', link: { status: 'active' } },
  { key: 'completed' as const, label: 'Completed', color: '#00aff0', link: { status: 'completed' } },
  { key: 'removed' as const, label: 'Removed', color: '#856404', link: { status: 'removed' } },
  { key: 'unsubscribed' as const, label: 'Unsubscribed', color: '#dc3545', link: { status: 'unsubscribed' } },
  { key: 'invalid' as const, label: 'Invalid Number', color: '#b02a37', link: { status: 'invalid' } },
  { key: 'failed' as const, label: 'Stopped After Retries', color: '#5c636a', link: { status: 'failed' } },
];

/** One box in a step card's grid: a send outcome or a lead kind, on that step. */
interface StepBox {
  label: string;
  value: number;
  color: string;
  rate?: number;
  link: Record<string, string>;
  /** Send counts are computed over the date range; lead counts link without it. */
  withRange: boolean;
}

export default function SmsAutomationStatsPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const userTz = useUserTimezone();
  const automationId = Number(id);

  const [dateFrom, setDateFrom] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() - 30);
    return d.toISOString().slice(0, 10);
  });
  const [dateTo, setDateTo] = useState(() => new Date().toISOString().slice(0, 10));

  const [expandedSteps, setExpandedSteps] = useState<Set<number>>(new Set());
  const [contentModalStep, setContentModalStep] = useState<StepStat | null>(null);
  const [loadingOverlay, setLoadingOverlay] = useState('');
  const [pauseConfirmOpen, setPauseConfirmOpen] = useState(false);
  const [refreshModalOpen, setRefreshModalOpen] = useState(false);
  const [refreshResult, setRefreshResult] = useState<{ added?: number; removed?: number; skipped?: number; total_contacts?: number; error?: string } | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['sms-automation-stats', automationId, dateFrom, dateTo],
    queryFn: () => marketingSvc.getSmsAutomationStats(automationId, { date_from: dateFrom, date_to: dateTo }),
    enabled: automationId > 0,
  });

  useEffect(() => {
    if (data && data.status === 'draft') {
      navigate(`/marketing/automations/sms/${automationId}`, { replace: true });
    }
  }, [data, automationId, navigate]);

  const isActive = data?.status === 'active';
  const isPaused = data?.status === 'paused';
  const summary = data?.summary ?? { totalSends: 0, sent: 0, delivered: 0, failed: 0, undelivered: 0 };
  const contactCounts = data?.contactCounts ?? { total: 0, active: 0, completed: 0, removed: 0, unsubscribed: 0, invalid: 0, failed: 0 };
  const stepStats: StepStat[] = data?.stepStats ?? [];
  // Steps taken out of the automation that still have sends. Absent from older API builds.
  const removedStepStats: RemovedStepStat[] = data?.removedStepStats ?? [];
  // Missing `leads` (an API that predates request-info links) reads as zeros.
  const leads = marketingSvc.leadCountsOf(data);

  // Removed steps' sends still count in the summary above, and now have their
  // own cards in Removed steps. Anything left over belongs to no step on record
  // at all, so the totals would stop adding up with no visible reason. Naming
  // the gap is cheaper than hunting it.
  const stepSendTotal = [...stepStats, ...removedStepStats].reduce((acc, s) => acc + (s.totalSends ?? 0), 0);
  const unattributedSends = Math.max(0, (summary.totalSends ?? 0) - stepSendTotal);

  // Every box opens the contacts behind its number.
  //
  // Send counts carry the date range. Every send number is computed over that
  // range, so a drill-down that dropped it would open an all-time list and show
  // contacts whose matching send falls outside the window the number came from
  // — a list that does not add up to the figure just clicked. Contact statuses
  // are current state and the contacts page filters leads all time, so those
  // links go without it.
  const openContacts = useCallback((params: Record<string, string | number | undefined>, withRange: boolean) => {
    navigate(contactsPath(
      `/marketing/automations/sms/${automationId}/contacts`,
      withRange ? { ...params, date_from: dateFrom, date_to: dateTo } : params,
    ));
  }, [navigate, automationId, dateFrom, dateTo]);

  const pauseMut = useMutation({
    mutationFn: () => marketingSvc.pauseSmsAutomation(automationId),
    onSuccess: () => {
      setLoadingOverlay('');
      qc.invalidateQueries({ queryKey: ['sms-automation-stats', automationId] });
      qc.invalidateQueries({ queryKey: ['marketing-history'] });
    },
    onError: () => { setLoadingOverlay(''); },
  });

  const resumeMut = useMutation({
    mutationFn: () => marketingSvc.activateSmsAutomation(automationId),
    onSuccess: () => {
      setLoadingOverlay('');
      qc.invalidateQueries({ queryKey: ['sms-automation-stats', automationId] });
      qc.invalidateQueries({ queryKey: ['marketing-history'] });
    },
    onError: (err: any) => { setLoadingOverlay(''); alert(err?.message || 'Failed to resume.'); },
  });

  const syncMut = useMutation({
    mutationFn: () => marketingSvc.syncSmsAutomationContacts(automationId),
    onSuccess: (result) => {
      setLoadingOverlay('');
      setRefreshResult({
        added: result.added,
        removed: result.removed ?? 0,
        skipped: result.skipped,
        total_contacts: contactCounts.total + (result.added ?? 0) - (result.removed ?? 0),
      });
      setRefreshModalOpen(true);
      qc.invalidateQueries({ queryKey: ['sms-automation-stats', automationId] });
    },
    onError: () => {
      setLoadingOverlay('');
      setRefreshResult({ error: 'Network error — please try again.' });
      setRefreshModalOpen(true);
    },
  });

  useEffect(() => {
    document.body.style.overflow = (refreshModalOpen || contentModalStep != null) ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [refreshModalOpen, contentModalStep]);

  function toggleStep(stepId: number) {
    setExpandedSteps(prev => {
      const next = new Set(prev);
      if (next.has(stepId)) next.delete(stepId);
      else next.add(stepId);
      return next;
    });
  }

  // The dialog owns the confirm button's spinner, so no page overlay here.
  // Errors are swallowed so the mutation's onError handles them and the promise
  // never rejects out of the dialog.
  async function confirmPause() {
    try {
      await pauseMut.mutateAsync();
    } catch {
      /* surfaced by pauseMut.onError */
    }
  }

  function handleResume() {
    setLoadingOverlay('Resuming automation...');
    resumeMut.mutate();
  }

  function handleRefresh() {
    setLoadingOverlay('Refreshing contacts...');
    syncMut.mutate();
  }

  if (isLoading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '60vh' }}>
        <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
      </div>
    );
  }

  if (!data) {
    return (
      <div style={{ textAlign: 'center', padding: '100px 20px', color: '#999' }}>
        <p style={{ fontSize: 18 }}>Automation not found.</p>
        <button type="button" onClick={() => navigate('/marketing/automations')} style={{ marginTop: 16, color: '#2e7d32', background: 'none', border: 'none', cursor: 'pointer', fontSize: 14 }}>
          Back to Automations
        </button>
      </div>
    );
  }

  const statusClass = data.status === 'active' ? 'sas-badge-active' : data.status === 'paused' ? 'sas-badge-paused' : 'sas-badge-stopped';

  /**
   * One step's card. A removed step renders the same card — its numbers still
   * open the contacts behind them — with its old position in a greyed badge
   * and a line saying when it was removed.
   */
  function renderStepCard(step: StepStat, isFirst: boolean, removal?: StepRemoval) {
    const attempts = step.totalSends ?? 0;
    const stepSent = step.sent ?? 0;
    const stepDelivered = step.delivered ?? 0;
    const stepFailed = step.failed ?? 0;
    const stepUndelivered = step.undelivered ?? 0;
    const deliveredRate = stepSent > 0 ? Math.round((stepDelivered / stepSent) * 1000) / 10 : 0;
    const failedRate = attempts > 0 ? Math.round((stepFailed / attempts) * 1000) / 10 : 0;
    const undeliveredRate = attempts > 0 ? Math.round((stepUndelivered / attempts) * 1000) / 10 : 0;
    const isExpanded = expandedSteps.has(step.stepId);
    // Active steps always show their lead counts (zeros when the API predates
    // them). A removed step shows them only when the API reported them, so an
    // absent figure is never passed off as a real zero.
    const showStepLeads = !removal || step.leads != null;
    const stepLeads = marketingSvc.leadCountsOf(step);
    // One figure for the step: its own leads and referrals together, opening
    // the contacts with "Created a lead" (either kind) from this step.
    const stepLeadTotal = stepLeads.recipient + stepLeads.referral;
    const stepBoxes: StepBox[] = [
      { label: 'Attempts', value: attempts, color: '#333', link: {}, withRange: true },
      // 'sent,delivered' because that is what this number counts.
      { label: 'Sent', value: stepSent, color: '#2e7d32', link: { sendStatus: 'sent,delivered' }, withRange: true },
      { label: 'Delivered', value: stepDelivered, color: '#8e44ad', rate: deliveredRate, link: { sendStatus: 'delivered' }, withRange: true },
      { label: 'Failed', value: stepFailed, color: stepFailed > 0 ? '#dc3545' : '#999', rate: failedRate, link: { sendStatus: 'failed' }, withRange: true },
      { label: 'Undelivered', value: stepUndelivered, color: stepUndelivered > 0 ? '#ff9800' : '#999', rate: undeliveredRate, link: { sendStatus: 'undelivered' }, withRange: true },
      ...(showStepLeads ? [
        { label: 'Leads', value: stepLeadTotal, color: stepLeadTotal > 0 ? '#fd7e14' : '#999', link: { lead: '1' }, withRange: false },
      ] : []),
    ];

    return (
      <div key={step.stepId} className="sas-step-card">
        <div className="sas-step-header" onClick={() => toggleStep(step.stepId)}>
          <div className="sas-step-header-left">
            <div
              className="sas-step-number"
              style={removal ? { background: '#adb5bd' } : undefined}
              title={removal ? `Removed — was step ${removal.previousStepOrder}` : undefined}
            >
              {removal ? removal.previousStepOrder : step.stepOrder}
            </div>
            <div className="sas-step-info">
              <h4>{step.messagePreview || '(Empty message)'}</h4>
              <span className="delay">{formatDelayText(step.delayMinutes, isFirst)}</span>
              {removal && <div className="removed-note">{describeRemoval(removal, userTz)}</div>}
            </div>
          </div>
          <div className="sas-step-mini">
            <div className="sas-step-mini-item">
              <div className="mini-value" style={{ color: '#2e7d32' }}>{fmtNum(stepSent)}</div>
              <div className="mini-label">Sent</div>
            </div>
            <div className="sas-step-mini-item">
              <div className="mini-value" style={{ color: '#8e44ad' }}>{deliveredRate}%</div>
              <div className="mini-label">Delivered</div>
            </div>
            <div className="sas-step-mini-item">
              <div className="mini-value" style={{ color: '#dc3545' }}>{fmtNum(stepFailed)}</div>
              <div className="mini-label">Failed</div>
            </div>
            {showStepLeads && (
              <div className="sas-step-mini-item">
                <div className="mini-value" style={{ color: stepLeadTotal > 0 ? '#fd7e14' : '#999' }}>
                  {fmtNum(stepLeadTotal)}
                </div>
                <div className="mini-label">Leads</div>
              </div>
            )}
          </div>
        </div>

        {isExpanded && (
          <div className="sas-step-body">
            <div className="sas-step-detail-grid">
              {stepBoxes.map((stat) => {
                const box = statBox(stat.value > 0 ? () => openContacts({ stepId: step.stepId, ...stat.link }, stat.withRange) : null);
                return (
                  <div key={stat.label} className={cn('sas-step-detail-stat', box.className)} {...box.props}>
                    <div className="sd-value" style={{ color: stat.color }}>{fmtNum(stat.value)}</div>
                    <div className="sd-label">{stat.label}</div>
                    {stat.rate != null && <div className="sd-rate">{stat.rate}%</div>}
                  </div>
                );
              })}
            </div>
            {/* "Sent" on a card is sent-or-delivered, so the two links
                overlap; said once here rather than in four tooltips. */}
            <div style={{ marginTop: 8, fontSize: 11, color: '#999' }}>
              Counts are attempts, not people — a retried step contributes more than one. Click any box for the contacts behind it.
            </div>

            {(step.messageBody || step.messagePreview) && (
              <div style={{ marginTop: 12, textAlign: 'right' }}>
                <button type="button" className="sas-action-btn" onClick={() => setContentModalStep(step)} style={{ fontSize: 12, padding: '5px 12px' }}>
                  View Message Content
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    );
  }

  return (
    <>
      <style>{`
        .sas-page { width: 100%; padding: 20px; margin: 0 auto; }

        .sas-channel-tag {
          display: inline-flex; align-items: center; padding: 3px 10px;
          border-radius: 4px; font-size: 13px; font-weight: 600; margin-left: 10px;
          vertical-align: middle; background: #e8f5e9; color: #2e7d32;
        }

        .sas-header {
          display: flex; justify-content: space-between; align-items: flex-start;
          margin-bottom: 20px; padding-bottom: 15px; border-bottom: 1px solid #e5e5e5;
          /* Pinned to the very top: the negative margin swallows the page's 20px
             top padding so the header starts flush at scroll 0 and never travels;
             opaque so content slides underneath. */
          position: sticky; top: 0; z-index: 100; background: #fff;
          margin-top: -20px; padding-top: 20px;
        }
        .sas-header h1 { margin: 0 0 8px 0; font-size: 24px; font-weight: 600; color: #333; }
        .sas-meta { display: flex; align-items: center; gap: 15px; font-size: 14px; color: #666; }

        .sas-badge {
          display: inline-flex; align-items: center; gap: 6px;
          padding: 4px 10px; border-radius: 12px; font-size: 12px;
          font-weight: 600; text-transform: capitalize;
        }
        .sas-badge::before { content: ''; width: 8px; height: 8px; border-radius: 50%; background: currentColor; }
        .sas-badge-active { background: #d4edda; color: #155724; }
        .sas-badge-paused { background: #fff3cd; color: #856404; }
        .sas-badge-stopped { background: #e9ecef; color: #666; }

        .sas-header-right { display: flex; flex-direction: column; align-items: flex-end; gap: 12px; }
        .sas-date-form { display: flex; gap: 10px; align-items: center; font-size: 13px; color: #666; }
        .sas-date-input {
          padding: 4px 8px; border: 1px solid #ddd; border-radius: 5px;
          font-size: 13px; background: white;
        }

        .sas-action-btn {
          padding: 6px 14px; font-size: 13px; font-weight: 600;
          color: #333; background: white; border: 1px solid #ddd;
          border-radius: 5px; cursor: pointer; transition: background 0.2s;
          display: inline-flex; align-items: center; gap: 5px;
        }
        .sas-action-btn:hover { background: #f5f5f5; }
        .sas-action-btn:disabled { opacity: 0.5; cursor: not-allowed; }
        .sas-action-btn.primary { background: #2e7d32; color: white; border-color: #2e7d32; }
        .sas-action-btn.primary:hover { background: #27692b; }

        .sas-contacts-link {
          display: inline-flex; align-items: center; gap: 8px; text-decoration: none;
          cursor: pointer; padding: 8px 14px; border-radius: 5px; transition: background 0.2s;
        }
        .sas-contacts-link:hover { background: #e8f5e9; }

        .sas-row-caption {
          font-size: 12px; font-weight: 700; color: #333; text-transform: uppercase;
          letter-spacing: .04em; margin: 18px 0 6px;
        }
        .sas-row-caption span {
          font-weight: 400; text-transform: none; letter-spacing: 0; color: #777; margin-left: 8px;
        }

        .sas-summary {
          display: grid; grid-template-columns: repeat(4, 1fr); gap: 0;
          background: white; border: 1px solid #e5e5e5; border-radius: 8px;
          margin-bottom: 25px; overflow: hidden;
        }
        /* 7 contact statuses and Leads: one row when wide, then 4x2 and 2x4. */
        .sas-summary.contacts { grid-template-columns: repeat(8, 1fr); }
        .sas-stat-card { padding: 20px 15px; text-align: center; border-right: 1px solid #e5e5e5; }
        .sas-stat-card:last-child { border-right: none; }
        .sas-stat-value { font-size: 28px; font-weight: 600; margin-bottom: 6px; }
        .sas-stat-value.zero { color: #999; }
        .sas-stat-label { font-size: 11px; font-weight: 600; text-transform: uppercase; color: #666; letter-spacing: 0.5px; }

        .sas-section {
          background: white; border: 1px solid #e5e5e5; border-radius: 8px;
          margin-bottom: 25px; overflow: hidden;
        }
        .sas-section-header {
          display: flex; align-items: center; gap: 8px;
          padding: 15px 20px; border-bottom: 1px solid #e5e5e5;
          font-weight: 600; color: #333;
        }
        .sas-info-icon {
          width: 16px; height: 16px; border-radius: 50%; background: #e5e5e5;
          color: #666; font-size: 11px; display: flex; align-items: center;
          justify-content: center; cursor: help;
        }

        .sas-step-card { background: #fff; border: 1px solid #e5e5e5; border-radius: 8px; margin-bottom: 12px; overflow: hidden; }
        .sas-step-header {
          display: flex; justify-content: space-between; align-items: center;
          padding: 16px 20px; cursor: pointer; transition: background 0.15s;
        }
        .sas-step-header:hover { background: #fafbfc; }
        .sas-step-header-left { display: flex; align-items: center; gap: 14px; }
        .sas-step-number {
          width: 32px; height: 32px; border-radius: 50%; background: #2e7d32;
          color: white; display: flex; align-items: center; justify-content: center;
          font-size: 14px; font-weight: 700; flex-shrink: 0;
        }
        .sas-step-info h4 { margin: 0 0 3px 0; font-size: 14px; font-weight: 600; color: #333; }
        .sas-step-info .delay { font-size: 12px; color: #999; }
        .sas-step-info .removed-note { font-size: 12px; color: #a3410a; margin-top: 2px; }
        .sas-step-mini { display: flex; flex-wrap: wrap; justify-content: flex-end; gap: 16px; }
        .sas-step-mini-item { text-align: center; }
        .sas-step-mini-item .mini-value { font-size: 18px; font-weight: 700; }
        .sas-step-mini-item .mini-label { font-size: 10px; font-weight: 600; text-transform: uppercase; color: #999; }

        .sas-step-body { padding: 0 20px 16px; border-top: 1px solid #f0f0f0; }
        .sas-step-detail-grid {
          /* 5 stats per step, 6 with its leads: one row when wide; the
             breakpoint below wraps them two to a row. */
          display: grid; grid-auto-flow: column; grid-auto-columns: 1fr; gap: 0;
          margin-top: 14px; border: 1px solid #e5e5e5; border-radius: 6px; overflow: hidden;
        }
        .sas-step-detail-stat { padding: 14px 10px; text-align: center; border-right: 1px solid #e5e5e5; }
        .sas-step-detail-stat:last-child { border-right: none; }
        .sas-step-detail-stat .sd-value { font-size: 22px; font-weight: 700; margin-bottom: 3px; }
        .sas-step-detail-stat .sd-label { font-size: 10px; font-weight: 600; text-transform: uppercase; color: #888; }
        .sas-step-detail-stat .sd-rate { font-size: 11px; color: #aaa; margin-top: 2px; }

        .sas-details-grid { display: grid; grid-template-columns: 200px 1fr; gap: 0; }
        .sas-detail-label {
          padding: 15px 20px; font-weight: 600; color: #333;
          background: #f8f9fa; border-bottom: 1px solid #e5e5e5; font-size: 14px;
        }
        .sas-detail-value { padding: 15px 20px; color: #666; border-bottom: 1px solid #e5e5e5; font-size: 14px; }
        .sas-detail-row:last-child .sas-detail-label,
        .sas-detail-row:last-child .sas-detail-value { border-bottom: none; }
        .sas-detail-value a { color: #2e7d32; text-decoration: none; }
        .sas-detail-value a:hover { text-decoration: underline; }

        .sas-empty-state { padding: 40px 20px; text-align: center; color: #999; }

        .sas-modal-overlay {
          display: flex; position: fixed; inset: 0; background: rgba(0,0,0,0.5);
          z-index: 9999; justify-content: center; align-items: center;
        }
        .sas-modal {
          background: #fff; border-radius: 10px; width: 90%; max-width: 380px;
          max-height: 85vh; display: flex; flex-direction: column;
          box-shadow: 0 8px 30px rgba(0,0,0,0.25);
        }
        .sas-modal-header {
          display: flex; justify-content: space-between; align-items: center;
          padding: 16px 20px; border-bottom: 1px solid #e5e5e5;
        }
        .sas-modal-header h3 { margin: 0; font-size: 16px; font-weight: 600; }
        .sas-modal-close {
          background: none; border: none; font-size: 22px; cursor: pointer;
          color: #999; padding: 0 4px; line-height: 1;
        }
        .sas-modal-close:hover { color: #333; }
        /* Centers the shared SmsPhoneMockup, matching the SMS editor's preview pane. */
        .sas-modal-body { flex: 1; overflow: auto; padding: 20px; display: flex; justify-content: center; }

        .sas-refresh-overlay {
          display: flex; position: fixed; inset: 0; background: rgba(0,0,0,0.45);
          z-index: 100000; justify-content: center; align-items: center;
        }
        .sas-refresh-modal {
          background: #fff; border-radius: 10px; width: 400px; max-width: 90%;
          box-shadow: 0 8px 30px rgba(0,0,0,0.25); overflow: hidden;
          animation: sasRefreshIn 0.25s ease;
        }
        @keyframes sasRefreshIn { from { opacity: 0; transform: scale(0.92); } to { opacity: 1; transform: scale(1); } }
        .sas-refresh-header {
          display: flex; justify-content: space-between; align-items: center;
          padding: 16px 20px; border-bottom: 1px solid #e5e5e5;
        }
        .sas-refresh-header h3 { margin: 0; font-size: 16px; font-weight: 600; color: #333; }
        .sas-refresh-body { padding: 24px 20px; text-align: center; }
        .sas-refresh-icon { font-size: 40px; margin-bottom: 12px; }
        .sas-refresh-message { font-size: 15px; color: #333; margin-bottom: 6px; font-weight: 600; }
        .sas-refresh-details { font-size: 13px; color: #666; line-height: 1.6; }
        .sas-refresh-stat {
          display: inline-flex; align-items: center; gap: 6px;
          padding: 4px 12px; border-radius: 16px; font-size: 13px; font-weight: 600; margin: 4px 4px;
        }
        .sas-refresh-stat.added { background: #d4edda; color: #155724; }
        .sas-refresh-stat.removed { background: #f8d7da; color: #721c24; }
        .sas-refresh-stat.unchanged { background: #e9ecef; color: #495057; }
        .sas-refresh-stat.error { background: #f8d7da; color: #721c24; }
        .sas-refresh-footer { margin-top: 16px; padding-top: 14px; border-top: 1px solid #e5e5e5; }
        .sas-refresh-total { font-size: 14px; color: #666; }
        .sas-refresh-total strong { color: #2e7d32; font-size: 16px; }

        .sas-loading-overlay {
          position: fixed; inset: 0; background: rgba(255,255,255,0.7);
          z-index: 99999; display: flex; justify-content: center; align-items: center;
          flex-direction: column; gap: 16px;
        }
        .sas-loading-spinner {
          width: 48px; height: 48px; border: 4px solid #e5e5e5; border-top-color: #2e7d32;
          border-radius: 50%; animation: sasSpin 0.7s linear infinite;
        }
        @keyframes sasSpin { to { transform: rotate(360deg); } }
        .sas-loading-text { font-size: 15px; font-weight: 600; color: #555; }

        @media (max-width: 1200px) {
          .sas-summary.contacts { grid-template-columns: repeat(4, 1fr); }
          .sas-summary.contacts .sas-stat-card:nth-child(4) { border-right: none; }
          .sas-summary.contacts .sas-stat-card:nth-child(n+5) { border-top: 1px solid #e5e5e5; }
        }
        @media (max-width: 768px) {
          .sas-summary, .sas-summary.contacts { grid-template-columns: repeat(2, 1fr); }
          .sas-stat-card:nth-child(2n) { border-right: none; }
          .sas-stat-card:nth-child(n+3) { border-top: 1px solid #e5e5e5; }
          .sas-details-grid { grid-template-columns: 1fr; }
          .sas-detail-label { border-bottom: none; padding-bottom: 5px; }
          .sas-detail-value { padding-top: 5px; }
          .sas-step-detail-grid { grid-auto-flow: row; grid-template-columns: repeat(2, 1fr); }
        }
      `}</style>

      {loadingOverlay && (
        <div className="sas-loading-overlay">
          <div className="sas-loading-spinner" />
          <div className="sas-loading-text">{loadingOverlay}</div>
        </div>
      )}

      <div className="sas-page">
        {/* Header */}
        <div className="sas-header">
          <div>
            <h1>{data.name} <span className="sas-channel-tag">SMS</span></h1>
            <div className="sas-meta">
              <span className={`sas-badge ${statusClass}`}>
                {data.status.charAt(0).toUpperCase() + data.status.slice(1)}
              </span>
              {isActive && (
                <button type="button" className="sas-action-btn" onClick={() => setPauseConfirmOpen(true)} style={{ fontSize: 12, padding: '4px 10px' }}>
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor"><rect x="6" y="4" width="4" height="16" rx="1" /><rect x="14" y="4" width="4" height="16" rx="1" /></svg>
                  Pause
                </button>
              )}
              {isPaused && (
                <>
                  <button type="button" className="sas-action-btn primary" onClick={handleResume} style={{ fontSize: 12, padding: '4px 10px' }}>Resume</button>
                  <button type="button" className="sas-action-btn" onClick={() => navigate(`/marketing/automations/sms/${automationId}`)} style={{ fontSize: 12, padding: '4px 10px' }}>
                    Edit Automation
                  </button>
                </>
              )}
              <span>Created {formatCreatedDate(data.createdAt)}</span>
            </div>
          </div>

          <div className="sas-header-right">
            <div className="sas-date-form">
              <label>From:</label>
              <DatePicker size="sm" ariaLabel="From" value={dateFrom} onChange={setDateFrom} wrapperClassName="w-36" />
              <label>To:</label>
              <DatePicker size="sm" ariaLabel="To" value={dateTo} onChange={setDateTo} wrapperClassName="w-36" />
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <span
                className="sas-contacts-link"
                onClick={() => navigate(`/marketing/automations/sms/${automationId}/contacts`)}
                title="View contacts"
              >
                <span style={{ fontSize: 20, fontWeight: 700, color: '#2e7d32' }}>{fmtNum(contactCounts.total)}</span>
                <span style={{ fontSize: 13, fontWeight: 600, color: '#333' }}>{contactCounts.total === 1 ? 'contact' : 'contacts'}</span>
              </span>
              <button type="button" className="sas-action-btn" onClick={handleRefresh} disabled={syncMut.isPending} style={{ fontSize: 12, padding: '5px 10px' }}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={syncMut.isPending ? { animation: 'sasSpin 0.7s linear infinite' } : undefined}><path d="M21.5 2v6h-6M2.5 22v-6h6M2 11.5a10 10 0 0 1 18.8-4.3M22 12.5a10 10 0 0 1-18.8 4.2" /></svg>
                Refresh
              </button>
            </div>
          </div>
        </div>

        {/* Send Summary Cards */}
        <div className="sas-row-caption">
          Messages <span>attempts sent {dateFrom} – {dateTo}; retries counted separately</span>
        </div>
        <div className="sas-summary">
          {SEND_CARDS.map(c => {
            const val = summary[c.key] ?? 0;
            const isZero = val === 0;
            const box = statBox(isZero ? null : () => openContacts(c.link, true));
            return (
              <div key={c.key} className={cn('sas-stat-card', box.className)} {...box.props}>
                <div className={`sas-stat-value${isZero ? ' zero' : ''}`} style={!isZero ? { color: c.color } : undefined}>{fmtNum(val)}</div>
                <div className="sas-stat-label">{c.label}</div>
              </div>
            );
          })}
        </div>

        {/* Contact Summary Cards */}
        <div className="sas-row-caption">
          People <span>where the sequence left each contact — all time, not filtered by the dates above. Leads are requests made through request-info links in these messages {dateFrom} – {dateTo}, referrals included</span>
        </div>
        <div className="sas-summary contacts">
          {CONTACT_CARDS.map(c => {
            const val = contactCounts[c.key] ?? 0;
            const isZero = val === 0;
            const box = statBox(isZero ? null : () => openContacts(c.link, false));
            return (
              <div key={c.key} className={cn('sas-stat-card', box.className)} {...box.props}>
                <div className={`sas-stat-value${isZero ? ' zero' : ''}`} style={!isZero ? { color: c.color } : undefined}>{fmtNum(val)}</div>
                <div className="sas-stat-label">{c.label}</div>
              </div>
            );
          })}
          {(() => {
            const val = leads.recipient + leads.referral;
            const isZero = val === 0;
            const box = statBox(isZero ? null : () => openContacts(LEAD_CARD.link, false));
            return (
              <div className={cn('sas-stat-card', box.className)} {...box.props}>
                <div className={`sas-stat-value${isZero ? ' zero' : ''}`} style={!isZero ? { color: LEAD_CARD.color } : undefined}>{fmtNum(val)}</div>
                <div className="sas-stat-label">{LEAD_CARD.label}</div>
              </div>
            );
          })()}
        </div>

        {/* Per-Step Analytics */}
        <div className="sas-section">
          <div className="sas-section-header">
            Step Performance
            <span className="sas-info-icon" title="Analytics for each step in this automation">i</span>
            {unattributedSends > 0 && (
              <span style={{ marginLeft: 'auto', fontSize: 11, fontWeight: 400, color: '#a3410a' }}>
                {fmtNum(unattributedSends)} send{unattributedSends === 1 ? '' : 's'} belong to no step on record — counted above, but under no step here.
              </span>
            )}
          </div>

          {stepStats.length > 0 ? (
            <div style={{ padding: '16px 20px' }}>
              {stepStats.map((step, idx) => renderStepCard(step, idx === 0))}
            </div>
          ) : (
            <div className="sas-empty-state">
              <div style={{ fontSize: 32, marginBottom: 10 }}>💬</div>
              <p>No steps configured for this automation.</p>
            </div>
          )}

          <RemovedStepsSection count={removedStepStats.length} noun="messages">
            {removedStepStats.map((step) => renderStepCard(step, step.previousStepOrder === 1, step))}
          </RemovedStepsSection>
        </div>

        {/* Details */}
        <div className="sas-section">
          <div className="sas-section-header">Details</div>
          <div className="sas-details-grid">
            <div className="sas-detail-row" style={{ display: 'contents' }}>
              <div className="sas-detail-label">Automation Name</div>
              <div className="sas-detail-value">{data.name}</div>
            </div>
            <div className="sas-detail-row" style={{ display: 'contents' }}>
              <div className="sas-detail-label">Description</div>
              <div className="sas-detail-value">{data.description || '—'}</div>
            </div>
            <div className="sas-detail-row" style={{ display: 'contents' }}>
              <div className="sas-detail-label">Status</div>
              <div className="sas-detail-value">
                <span className={`sas-badge ${statusClass}`}>{data.status.charAt(0).toUpperCase() + data.status.slice(1)}</span>
              </div>
            </div>
            <div className="sas-detail-row" style={{ display: 'contents' }}>
              <div className="sas-detail-label">Entry List</div>
              <div className="sas-detail-value">
                {data.entryListId ? (
                  <Link to={`/marketing/contact-lists/${data.entryListId}/details`} title="Open in Contact Lists">
                    {data.entryListName || `List #${data.entryListId}`}
                  </Link>
                ) : (data.entryListName || '—')}
              </div>
            </div>
            <div className="sas-detail-row" style={{ display: 'contents' }}>
              <div className="sas-detail-label">Steps</div>
              <div className="sas-detail-value">{data.stepCount} SMS step{data.stepCount !== 1 ? 's' : ''}</div>
            </div>
            <div className="sas-detail-row" style={{ display: 'contents' }}>
              <div className="sas-detail-label">Exit Criteria</div>
              <div className="sas-detail-value">{EXIT_CRITERIA_LABELS[data.exitCriteria] ?? '—'}</div>
            </div>
            <div className="sas-detail-row" style={{ display: 'contents' }}>
              <div className="sas-detail-label">Allow Re-entry</div>
              <div className="sas-detail-value">{data.allowReentry ? 'Yes — contacts who leave and return will restart the automation' : 'No — contacts can only enter once'}</div>
            </div>
            <div className="sas-detail-row" style={{ display: 'contents' }}>
              <div className="sas-detail-label">Contacts</div>
              <div className="sas-detail-value">
                <Link to={`/marketing/automations/sms/${automationId}/contacts`} className="sas-contacts-link" style={{ padding: '4px 8px' }}>
                  <span style={{ fontSize: 18, fontWeight: 600, color: '#2e7d32' }}>{fmtNum(contactCounts.total)}</span>
                  <span>{contactCounts.total === 1 ? 'contact' : 'contacts'}</span>
                </Link>
                <span style={{ marginLeft: 12, color: '#999', fontSize: 12 }}>
                  ({fmtNum(contactCounts.active)} active, {fmtNum(contactCounts.completed)} completed, {fmtNum(contactCounts.removed)} removed)
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* History */}
        <MarketingHistoryCard type="sms_automation" id={automationId} className="mb-[25px]" />
      </div>

      <PauseAutomationDialog
        isOpen={pauseConfirmOpen}
        channel="sms"
        automationName={data.name}
        activeContacts={contactCounts.active}
        onClose={() => setPauseConfirmOpen(false)}
        onConfirm={confirmPause}
      />

      {/* Message Content Modal */}
      {contentModalStep && (
        <div className="sas-modal-overlay" onClick={e => { if (e.target === e.currentTarget) setContentModalStep(null); }}>
          <div className="sas-modal">
            <div className="sas-modal-header">
              <h3>Step {contentModalStep.stepOrder} Message</h3>
              <button className="sas-modal-close" onClick={() => setContentModalStep(null)}>&times;</button>
            </div>
            <div className="sas-modal-body">
              <SmsPhoneMockup
                messageBody={contentModalStep.messageBody || contentModalStep.messagePreview}
                mediaUrls={contentModalStep.mediaUrls ?? []}
              />
            </div>
          </div>
        </div>
      )}

      {/* Refresh Modal */}
      {refreshModalOpen && (
        <div className="sas-refresh-overlay" onClick={e => { if (e.target === e.currentTarget) setRefreshModalOpen(false); }}>
          <div className="sas-refresh-modal">
            <div className="sas-refresh-header">
              <h3>Refresh Results</h3>
              <button className="sas-modal-close" onClick={() => setRefreshModalOpen(false)}>&times;</button>
            </div>
            <div className="sas-refresh-body">
              {refreshResult?.error ? (
                <>
                  <div className="sas-refresh-icon">⚠️</div>
                  <div className="sas-refresh-message">Refresh Failed</div>
                  <div className="sas-refresh-details"><span className="sas-refresh-stat error">{refreshResult.error}</span></div>
                </>
              ) : (refreshResult?.added ?? 0) > 0 || (refreshResult?.removed ?? 0) > 0 ? (
                <>
                  <div className="sas-refresh-icon">✅</div>
                  <div className="sas-refresh-message">Contacts Updated</div>
                  <div className="sas-refresh-details" style={{ marginTop: 12 }}>
                    {(refreshResult?.added ?? 0) > 0 && <span className="sas-refresh-stat added">+ {refreshResult!.added} added</span>}
                    {(refreshResult?.removed ?? 0) > 0 && <span className="sas-refresh-stat removed">{refreshResult!.removed} removed</span>}
                  </div>
                  <div className="sas-refresh-footer">
                    <span className="sas-refresh-total">Total contacts: <strong>{fmtNum(refreshResult?.total_contacts ?? contactCounts.total)}</strong></span>
                  </div>
                </>
              ) : (
                <>
                  <div className="sas-refresh-icon">👍</div>
                  <div className="sas-refresh-message">No Changes</div>
                  <div className="sas-refresh-details"><span className="sas-refresh-stat unchanged">Contact list is already up to date</span></div>
                  <div className="sas-refresh-footer">
                    <span className="sas-refresh-total">Total contacts: <strong>{fmtNum(refreshResult?.total_contacts ?? contactCounts.total)}</strong></span>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
